import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DealStatus } from 'components/common/types/Deal.types';
import { Store } from 'components/common/types/Store.types';
import { isSamePlannerFamily } from 'utils/isPlannerPath';
import { isDealViewSwitchPending } from 'utils/dealSwitchView';
import { changeFormParams, changeIsNewDeal, changeIsNewLineInEndedDeal, resetState } from './reducer';
import { getIsNewLine } from './selectors';
import { DealManagement } from './types';

export const useDealManagement = ({ isNewDeal = false }): DealManagement => {
  const dispatch = useDispatch();
  const dealManagementState = useSelector((state: Store) => state.dealManagement);
  const { type: publisherType } = useSelector((storeState: Store) => storeState.publisher.configuration);

  const bookingStatus = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const isNewLine = useSelector(getIsNewLine);
  const isDealEnded = useMemo(() => [DealStatus.ENDED, DealStatus.TERMINATED].includes(bookingStatus), [bookingStatus]);

  const {
    commonDeal: { bookingStatusCode, dealId },
    isDuplicated,
  } = dealManagementState;

  useEffect(() => {
    // Mode-switch remounts keep isNewDeal; skip so drawer/dealType are not reset mid-session.
    if (isNewDeal && !isDuplicated && !dealManagementState.isNewDeal) {
      dispatch(changeIsNewDeal(publisherType));
    }
  }, [isNewDeal]);

  useEffect(() => {
    if (dealId) {
      dispatch(changeFormParams({ isForecastedAllocation: bookingStatusCode === DealStatus.PENDING_APPROVAL }));
    }
  }, [dealId, bookingStatusCode]);

  useEffect(() => {
    const mountPathname = window.location.pathname;

    return () => {
      if (isSamePlannerFamily(mountPathname, window.location.pathname)) return;
      if (isDealViewSwitchPending()) return;

      dispatch(resetState());
    };
  }, []);

  useEffect(() => {
    if (isDealEnded && isNewLine) {
      changeIsNewLineInEndedDeal();
    }
  }, [isDealEnded, isNewLine]);

  return dealManagementState;
};
